import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, CheckCircle2, Circle, ChevronRight,
  Package, Truck, CreditCard, Factory, FlaskConical, ShoppingCart,
  MapPin, Camera, AlertTriangle, Clock, ThumbsUp,
  ReceiptText, Activity, XCircle, type LucideIcon,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ==================== TYPES ====================

interface TimelineEntry {
  stage: string;
  action: string;
  notes?: string | null;
  userId?: string | null;
  timestamp: string;
}

interface FulfilmentData {
  timeline?: TimelineEntry[];
  discountNotes?: string;
  warehouseLocation?: string;
  advancePaymentStatus?: string;
  advancePaymentNotes?: string;
  manufacturingStage?: string;
  manufacturingNotes?: string;
  qcStage?: string;
  qcResult?: string;
  qcInspector?: string;
  qcNotes?: string;
  qcRework?: string;
  remainingPaymentStatus?: string;
  remainingPaymentNotes?: string;
  fixedVendor?: boolean;
  vendorName?: string;
  poNumber?: string;
  grnNumber?: string;
  grnReceivedQty?: number;
  grnInspectionStatus?: string;
  grnInspector?: string;
  grnNotes?: string;
  paymentStatus?: string;
  paymentNotes?: string;
  allocatedQty?: number;
  dispatchPhotos?: string[];
  carrier?: string;
  vehicleNo?: string;
  driverName?: string;
  shipmentId?: string;
  estimatedDelivery?: string;
  deliveryPhotos?: string[];
  customerInvoiceRef?: string;
  resolutionType?: string;
  adjustmentNotes?: string;
  debitNoteRef?: string;
  creditNoteRef?: string;
  [key: string]: unknown;
}

interface SalesOrder {
  id: string;
  number: string;
  status: string;
  fulfilmentStage?: string | null;
  fulfilmentPath?: string | null;
  fulfilmentData?: FulfilmentData | null;
  total: number;
  subtotal: number;
  contact?: { firstName: string; lastName: string } | null;
  account?: { name: string } | null;
  items: Array<{
    id: string;
    productId?: string;
    description: string;
    quantity: number;
    rate: number;
    amount: number;
    fulfillmentSource: string;
    fulfilledQty?: number;
    unit?: string | null;
    discount?: number;
  }>;
}

// ==================== STAGE DEFINITIONS ====================

const COMMON_STAGES_START = ['discount', 'inventory'];
const COMMON_STAGES_END = ['dispatch', 'transit', 'delivery', 'resolution', 'fulfilled'];

const PATH_STAGES: Record<string, string[]> = {
  manufactured: ['advance_payment', 'manufacturing', 'qc', 'remaining_payment'],
  traded: ['procurement', 'grn_qc', 'payment_check'],
  in_stock: ['allocate', 'payment_check'],
};

function getFullStageList(path: string | null | undefined): string[] {
  const middle = path ? (PATH_STAGES[path] || []) : [];
  return [...COMMON_STAGES_START, ...middle, ...COMMON_STAGES_END];
}

const SO_FLOW_STAGES = ['payment_check', 'inventory', 'allocate', 'dispatch', 'delivery', 'resolution', 'fulfilled'];

function mapBackendStageToUi(stage?: string, fulfilledAt?: string | null): string {
  if (fulfilledAt) return 'fulfilled';
  switch (stage) {
    case 'on_hold':
      return 'advance_payment';
    case 'advance_pending':
      return 'remaining_payment';
    case 'balance_pending':
      return 'inventory';
    case 'vendor_selection':
    case 'quotation_requested':
    case 'po_created':
    case 'procurement':
      return 'procurement';
    case 'inventory_allocated':
      return 'allocate';
    case 'manufacturing_plan':
      return 'manufacturing';
    case 'qc_in_progress':
    case 'qc1':
    case 'qc2':
    case 'qc3':
    case 'qc_failed':
      return 'qc';
    case 'ready_for_dispatch':
      return 'dispatch';
    case 'in_transit':
      return 'transit';
    case 'delivery_validation':
      return 'resolution';
    case 'payment_pending':
      return 'payment_check';
    case 'awaiting_stock':
    case 'in_production':
      return 'inventory';
    case 'awaiting_dispatch':
      return 'dispatch';
    case 'dispatched':
      return 'delivery';
    case 'delivered':
      return 'resolution';
    case 'fulfilled':
      return 'fulfilled';
    default:
      return 'inventory';
  }
}

const STAGE_LABELS: Record<string, string> = {
  discount: 'Discount',
  inventory: 'Inventory Check',
  advance_payment: '50% Advance',
  manufacturing: 'Manufacturing',
  qc: 'Quality Control',
  remaining_payment: 'Remaining 50%',
  procurement: 'Procurement / PO',
  grn_qc: 'GRN & QC',
  payment_check: 'Payment Check',
  allocate: 'Allocate Stock',
  dispatch: 'Dispatch',
  transit: 'In Transit',
  delivery: 'Delivery',
  resolution: 'Resolution',
  fulfilled: 'Fulfilled',
};

const STAGE_ICONS: Record<string, LucideIcon> = {
  discount: ReceiptText,
  inventory: Package,
  advance_payment: CreditCard,
  manufacturing: Factory,
  qc: FlaskConical,
  remaining_payment: CreditCard,
  procurement: ShoppingCart,
  grn_qc: Package,
  payment_check: CreditCard,
  allocate: MapPin,
  dispatch: Truck,
  transit: Truck,
  delivery: CheckCircle2,
  resolution: AlertTriangle,
  fulfilled: ThumbsUp,
};

// ==================== HELPER ====================

function Alert({ type, message }: { type: 'success' | 'error' | 'info'; message: string }) {
  const styles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
  };
  return (
    <div className={`rounded-lg border px-4 py-3 text-sm ${styles[type]}`}>{message}</div>
  );
}

function roundTwo(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

function SectionCard({ icon: Icon, title, description, children }: {
  icon: LucideIcon;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border p-6" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
      <div className="mb-5 flex items-start gap-3">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg" style={{ background: 'var(--bg-muted)' }}>
          <Icon size={18} className="text-[var(--ink-0)]" />
        </div>
        <div>
          <h3 className="text-base font-semibold" style={{ color: 'var(--text-primary)' }}>{title}</h3>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{description}</p>
        </div>
      </div>
      {children}
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="mb-1 block text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
      {children}
    </label>
  );
}

function Input({ value, onChange, placeholder, type = 'text' }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  className?: string;
}) {
  return (
    <input
      type={type}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]"
      style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
    />
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--ink-0)]"
      style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
    />
  );
}

function RadioGroup({ options, value, onChange }: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-3">
      {options.map(opt => (
        <label key={opt.value} className="flex cursor-pointer items-center gap-2">
          <input
            type="radio"
            name={`radio-${options[0].value}`}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="accent-[var(--ink-0)]"
          />
          <span className="text-sm" style={{ color: 'var(--text-primary)' }}>{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function PrimaryButton({ onClick, disabled, loading, children }: {
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled || loading}
      className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[var(--ink-1)] disabled:opacity-50"
    >
      {loading && <Loader2 size={14} className="animate-spin" />}
      {children}
    </button>
  );
}

// ==================== STAGE PANELS ====================

function DiscountPanel({ so, fd, onAdvance }: { so: SalesOrder; fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [notes, setNotes] = useState(fd.discountNotes || '');
  const [saving, setSaving] = useState(false);
  const [itemDiscounts, setItemDiscounts] = useState<Record<string, { applied: boolean; notes: string }>>(() => {
    const initial: Record<string, { applied: boolean; notes: string }> = {};
    so.items.forEach(item => {
      initial[item.id] = {
        applied: Number(item.discount || 0) > 0,
        notes: '',
      };
    });
    return initial;
  });

  const toggleItemDiscount = (itemId: string) => {
    setItemDiscounts(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], applied: !prev[itemId].applied },
    }));
  };

  const updateItemDiscountNotes = (itemId: string, notes: string) => {
    setItemDiscounts(prev => ({
      ...prev,
      [itemId]: { ...prev[itemId], notes },
    }));
  };

  const totalDiscount = so.items.reduce((sum, item) => {
    return sum + (itemDiscounts[item.id]?.applied ? Number(item.discount || 0) : 0);
  }, 0);

  return (
    <SectionCard icon={ReceiptText} title="Finalise Discounts" description="Review per-item discounts. Toggle to apply or skip each discount.">
      <div className="mb-4 overflow-hidden rounded-lg border" style={{ borderColor: 'var(--border-primary)' }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-medium uppercase tracking-wider" style={{ background: 'var(--bg-muted)', color: 'var(--text-muted)' }}>
              <th className="px-4 py-2 w-8">Apply</th>
              <th className="px-4 py-2">Item</th>
              <th className="px-4 py-2 text-right">Qty</th>
              <th className="px-4 py-2 text-right">Rate</th>
              <th className="px-4 py-2 text-right">Discount</th>
              <th className="px-4 py-2 text-right">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {so.items.map(item => (
              <tr key={item.id} style={{ color: 'var(--text-primary)' }}>
                <td className="px-4 py-2">
                  <input
                    type="checkbox"
                    checked={itemDiscounts[item.id]?.applied ?? false}
                    onChange={() => toggleItemDiscount(item.id)}
                    className="h-4 w-4 rounded accent-[var(--ink-0)]"
                  />
                </td>
                <td className="px-4 py-2.5">{item.description}</td>
                <td className="px-4 py-2.5 text-right">{Number(item.quantity)} {item.unit || ''}</td>
                <td className="px-4 py-2.5 text-right">{formatINR(Number(item.rate))}</td>
                <td className="px-4 py-2.5 text-right">
                  {Number(item.discount) > 0 ? (
                    <span className={itemDiscounts[item.id]?.applied ? 'text-green-600 font-medium' : 'line-through text-gray-400'}>
                      {formatINR(Number(item.discount))}
                    </span>
                  ) : '—'}
                </td>
                <td className="px-4 py-2.5 text-right font-medium">{formatINR(Number(item.amount))}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{ background: 'var(--bg-muted)' }}>
              <td colSpan={4} className="px-4 py-3 text-right font-semibold" style={{ color: 'var(--text-primary)' }}>Total Discount Applied:</td>
              <td className="px-4 py-3 text-right font-bold text-green-600">{formatINR(totalDiscount)}</td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
      <div className="mb-4">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={setNotes} placeholder="Any notes on discounts..." />
      </div>
      <div className="flex gap-3">
        <PrimaryButton loading={saving} onClick={async () => {
          setSaving(true);
          const finalDiscounts = so.items.map(item => ({
            itemId: item.id,
            applied: itemDiscounts[item.id]?.applied,
            amount: itemDiscounts[item.id]?.applied ? Number(item.discount) : 0,
            notes: itemDiscounts[item.id]?.notes || '',
          }));
          await onAdvance({ discountNotes: notes, itemDiscounts: finalDiscounts, totalDiscount }, 'Discounts finalised', notes);
          setSaving(false);
        }}>Finalise Discounts</PrimaryButton>
        <button
          onClick={async () => { setSaving(true); await onAdvance({}, 'Discount step skipped'); setSaving(false); }}
          className="rounded-lg border px-5 py-2.5 text-sm font-medium transition-colors hover:opacity-80"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
        >Skip</button>
      </div>
    </SectionCard>
  );
}

function InventoryPanel({ so, fd, onAdvance }: { so: SalesOrder; fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string, path?: string, nextStage?: string) => Promise<void> }) {
  const [path, setPath] = useState<string>(fd.fulfilmentPath as string || '');
  const [warehouseLocation, setWarehouseLocation] = useState(fd.warehouseLocation || '');
  const [saving, setSaving] = useState(false);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [inventoryData, setInventoryData] = useState<Record<string, { warehouse: string; quantity: number; location: string }>>({});

  const sourceMap: Record<string, string> = { in_stock: 'In Stock', procure: 'Traded/Procure', manufacture: 'Manufacture' };

  const warehouseOptions: Record<string, { label: string; details: string }> = {
    'Warehouse 1': { label: 'Warehouse 1 (Traded Items)', details: 'Finished goods from vendors' },
    'Warehouse 2': { label: 'Warehouse 2 (Manufactured Items)', details: 'In-house production' },
    'main': { label: 'Main Warehouse', details: 'General storage' },
  };

  const checkInventory = async () => {
    setLoadingInventory(true);
    try {
      const inventoryMap: Record<string, { warehouse: string; quantity: number; location: string }> = {};
      for (const item of so.items) {
        if (item.productId) {
          try {
            const res = await api.get(`/warehouse/inventory?productId=${item.productId}`);
            if (res.data.data && res.data.data.length > 0) {
              const inv = res.data.data[0];
              inventoryMap[item.id] = {
                warehouse: inv.warehouse || 'main',
                quantity: Number(inv.quantity) || 0,
                location: inv.location || '',
              };
            }
          } catch {
            inventoryMap[item.id] = { warehouse: 'Not Found', quantity: 0, location: '' };
          }
        } else {
          inventoryMap[item.id] = { warehouse: 'N/A', quantity: 0, location: '' };
        }
      }
      setInventoryData(inventoryMap);
    } catch (err) {
      console.error('Failed to fetch inventory', err);
    } finally {
      setLoadingInventory(false);
    }
  };

  return (
    <SectionCard icon={Package} title="Inventory Check" description="Check stock availability, view warehouse locations, and select the fulfilment path.">
      <div className="mb-4 space-y-2">
        <div className="flex items-center justify-between mb-3">
          <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Item Stock Overview</span>
          <button
            onClick={checkInventory}
            disabled={loadingInventory}
            className="text-xs text-[var(--ink-0)] hover:underline disabled:opacity-50"
          >
            {loadingInventory ? 'Checking...' : 'Refresh Stock'}
          </button>
        </div>
        {so.items.map(item => (
          <div key={item.id} className="rounded-lg border px-4 py-3" style={{ borderColor: 'var(--border-primary)' }}>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{item.description}</span>
              <span className="rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>
                {sourceMap[item.fulfillmentSource] || item.fulfillmentSource}
              </span>
            </div>
            {inventoryData[item.id] && (
              <div className="flex items-center gap-4 text-xs" style={{ color: 'var(--text-muted)' }}>
                <span>Warehouse: <strong style={{ color: 'var(--text-primary)' }}>{inventoryData[item.id].warehouse}</strong></span>
                <span>Qty: <strong style={{ color: inventoryData[item.id].quantity >= Number(item.quantity) ? 'var(--text-primary)' : 'var(--text-error)' }}>{inventoryData[item.id].quantity}</strong> / {Number(item.quantity)}</span>
                {inventoryData[item.id].location && <span>Location: {inventoryData[item.id].location}</span>}
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mb-4">
        <Label>Select Fulfilment Path</Label>
        <div className="flex gap-3">
          {[
            { value: 'in_stock', label: 'In Stock' },
            { value: 'manufactured', label: 'Manufactured' },
            { value: 'traded', label: 'Traded / Procure' },
          ].map(opt => (
            <button
              key={opt.value}
              onClick={() => {
                setPath(opt.value);
                if (opt.value === 'traded') setWarehouseLocation('Warehouse 1');
                else if (opt.value === 'manufactured') setWarehouseLocation('Warehouse 2');
                else setWarehouseLocation('');
              }}
              className="flex-1 rounded-lg border py-2.5 text-sm font-medium transition-all"
              style={{
                borderColor: path === opt.value ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
                background: path === opt.value ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                color: path === opt.value ? 'var(--primary-700, #1d4ed8)' : 'var(--text-secondary)',
              }}
            >{opt.label}</button>
          ))}
        </div>
      </div>
      {path && (
        <div className="mb-4 rounded-lg border p-4" style={{ background: 'var(--bg-muted)', borderColor: 'var(--border-primary)' }}>
          <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Warehouse Assignment</p>
          <p className="mt-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
            {path === 'traded' && 'Warehouse 1 - Traded Items (from vendors)'}
            {path === 'manufactured' && 'Warehouse 2 - Manufactured Items (in-house production)'}
            {path === 'in_stock' && (warehouseLocation || 'Select location below')}
          </p>
        </div>
      )}
      {path === 'in_stock' && (
        <div className="mb-4">
          <Label>Warehouse / Rack Location</Label>
          <Input value={warehouseLocation} onChange={setWarehouseLocation} placeholder="e.g. Warehouse A, Rack B3" />
        </div>
      )}
      <PrimaryButton disabled={!path} loading={saving} onClick={async () => {
        if (!path) return;
        setSaving(true);
        const nextStage = path === 'in_stock' ? 'allocate' : path === 'manufactured' ? 'advance_payment' : 'procurement';
        const warehouse = path === 'traded' ? 'Warehouse 1' : path === 'manufactured' ? 'Warehouse 2' : warehouseLocation;
        await onAdvance({ warehouseLocation: warehouse, inventoryChecked: true }, 'Inventory checked — path: ' + path + ', warehouse: ' + warehouse, undefined, path, nextStage);
        setSaving(false);
      }}>Confirm Inventory Check</PrimaryButton>
    </SectionCard>
  );
}

function PaymentPanel({ title, description, icon: Icon, amountLabel, amount, statusKey, notesKey, fd, onAdvance, actionLabel }: {
  title: string;
  description: string;
  icon: LucideIcon;
  amountLabel: string;
  amount: number;
  statusKey: keyof FulfilmentData;
  notesKey: keyof FulfilmentData;
  fd: FulfilmentData;
  onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void>;
  actionLabel: string;
}) {
  const [status, setStatus] = useState<string>((fd[statusKey] as string) || '');
  const [notes, setNotes] = useState<string>((fd[notesKey] as string) || '');
  const [saving, setSaving] = useState(false);

  return (
    <SectionCard icon={Icon} title={title} description={description}>
      <div className="mb-4 rounded-lg p-4" style={{ background: 'var(--bg-muted)' }}>
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>{amountLabel}</p>
        <p className="mt-1 text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(amount)}</p>
      </div>
      <div className="mb-4">
        <Label>Payment Status</Label>
        <RadioGroup
          value={status}
          onChange={setStatus}
          options={[{ value: 'received', label: 'Received' }, { value: 'on_hold', label: 'On Hold / Chase' }]}
        />
      </div>
      {status === 'on_hold' && (
        <div className="mb-4 rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          Payment is on hold. Follow up with the customer and update once received.
        </div>
      )}
      <div className="mb-4">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={setNotes} placeholder="Reference number, date received, bank, etc." />
      </div>
      <PrimaryButton disabled={!status} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ [statusKey]: status, [notesKey]: notes } as Partial<FulfilmentData>, actionLabel + ': ' + status, notes);
        setSaving(false);
      }}>{actionLabel}</PrimaryButton>
    </SectionCard>
  );
}

function ManufacturingPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [stage, setStage] = useState(fd.manufacturingStage || '');
  const [notes, setNotes] = useState(fd.manufacturingNotes || '');
  const [saving, setSaving] = useState(false);

  const kanbanStages = [
    { value: 'rm_inspection', label: 'RM Inspection', desc: 'Raw material verification' },
    { value: 'cutting', label: 'Cutting', desc: 'Sheet cutting & preparation' },
    { value: 'forming', label: 'Forming', desc: 'Tank forming process' },
    { value: 'welding', label: 'Welding', desc: 'Seam welding' },
    { value: 'polishing', label: 'Polishing', desc: 'Surface finishing' },
    { value: 'assembly', label: 'Assembly', desc: 'Component assembly' },
    { value: 'testing', label: 'Testing', desc: 'Pressure testing' },
    { value: 'packaging', label: 'Packaging', desc: 'Final packaging' },
    { value: 'qc1', label: 'QC-1', desc: 'First quality check' },
    { value: 'qc2', label: 'QC-2', desc: 'Second quality check' },
    { value: 'qc3', label: 'QC-3', desc: 'Final quality check' },
    { value: 'dispatch', label: 'Dispatch Ready', desc: 'Ready for dispatch' },
  ];

  const currentStageIndex = kanbanStages.findIndex(s => s.value === stage);

  return (
    <SectionCard icon={Factory} title="Manufacturing Kanban" description="Track production stages from RM inspection to dispatch. Click a stage to advance.">
      <div className="mb-4">
        <Label>Current Production Stage</Label>
        <div className="grid grid-cols-4 gap-2">
          {kanbanStages.map((s, idx) => {
            const isCompleted = idx < currentStageIndex;
            const isCurrent = idx === currentStageIndex;
            const isFuture = idx > currentStageIndex;
            return (
              <button
                key={s.value}
                onClick={() => setStage(s.value)}
                className="rounded-lg border px-3 py-2.5 text-left text-xs font-medium transition-all"
                style={{
                  borderColor: isCompleted ? '#22c55e' : isCurrent ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)',
                  background: isCompleted ? '#f0fdf4' : isCurrent ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)',
                  color: isCompleted ? '#16a34a' : isCurrent ? 'var(--primary-700, #1d4ed8)' : 'var(--text-secondary)',
                  opacity: isFuture ? 0.6 : 1,
                }}
              >
                <div className="flex items-center gap-1.5">
                  {isCompleted && <CheckCircle2 size={12} className="text-green-600" />}
                  {!isCompleted && <Circle size={12} style={{ color: isCurrent ? undefined : 'var(--text-muted)' }} />}
                  <span className="truncate">{s.label}</span>
                </div>
                <div className="text-[10px] opacity-70 mt-0.5 truncate">{s.desc}</div>
              </button>
            );
          })}
        </div>
      </div>
      {currentStageIndex >= 0 && (
        <div className="mb-4 rounded-lg border p-4" style={{ background: 'var(--bg-muted)', borderColor: 'var(--border-primary)' }}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Progress</p>
              <p className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{currentStageIndex + 1} / {kanbanStages.length}</p>
            </div>
            <div className="text-right">
              <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Current Stage</p>
              <p className="text-sm font-semibold text-[var(--ink-0)]">{kanbanStages[currentStageIndex]?.label}</p>
            </div>
          </div>
          <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full bg-green-500 transition-all"
              style={{ width: `${((currentStageIndex + 1) / kanbanStages.length) * 100}%` }}
            />
          </div>
        </div>
      )}
      <div className="mb-4">
        <Label>Notes / Production Plan Reference</Label>
        <Textarea value={notes} onChange={setNotes} placeholder="BOM number, work order, special instructions..." />
      </div>
      <div className="flex gap-3">
        <PrimaryButton
          disabled={!stage || currentStageIndex >= kanbanStages.length - 1}
          loading={saving}
          onClick={async () => {
            setSaving(true);
            const nextStageIndex = currentStageIndex + 1;
            const nextStage = nextStageIndex < kanbanStages.length ? kanbanStages[nextStageIndex].value : stage;
            await onAdvance({ manufacturingStage: nextStage, manufacturingNotes: notes }, `Advanced to: ${kanbanStages[nextStageIndex]?.label || stage}`, notes);
            setSaving(false);
          }}
        >
          Advance to Next Stage
        </PrimaryButton>
        <button
          onClick={async () => {
            setSaving(true);
            await onAdvance({ manufacturingStage: stage, manufacturingNotes: notes }, 'Manufacturing stage updated: ' + stage, notes);
            setSaving(false);
          }}
          className="rounded-lg border px-5 py-2.5 text-sm font-medium transition-colors hover:opacity-80"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-secondary)' }}
        >Save Stage</button>
      </div>
    </SectionCard>
  );
}

function QCPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [qcStage, setQcStage] = useState(fd.qcStage || '');
  const [result, setResult] = useState(fd.qcResult || '');
  const [inspector, setInspector] = useState(fd.qcInspector || '');
  const [notes, setNotes] = useState(fd.qcNotes || '');
  const [rework, setRework] = useState(fd.qcRework || '');
  const [saving, setSaving] = useState(false);

  return (
    <SectionCard icon={FlaskConical} title="Quality Control" description="Record QC inspection result. Failed items go to rework or remanufacturing.">
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label>QC Stage</Label>
          <RadioGroup value={qcStage} onChange={setQcStage} options={[
            { value: 'QC-1', label: 'QC-1' },
            { value: 'QC-2', label: 'QC-2' },
            { value: 'QC-3', label: 'QC-3' },
          ]} />
        </div>
        <div>
          <Label>Result</Label>
          <RadioGroup value={result} onChange={setResult} options={[
            { value: 'pass', label: 'Pass' },
            { value: 'fail', label: 'Fail' },
          ]} />
        </div>
      </div>
      <div className="mb-4">
        <Label>Inspector Name</Label>
        <Input value={inspector} onChange={setInspector} placeholder="Inspector's name" />
      </div>
      {result === 'fail' && (
        <div className="mb-4">
          <Label>Failure Action</Label>
          <RadioGroup value={rework} onChange={setRework} options={[
            { value: 'rework', label: 'Rework' },
            { value: 'remanufacture', label: 'Reject & Remanufacture' },
          ]} />
        </div>
      )}
      <div className="mb-4">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={setNotes} placeholder="Defects found, inspection notes..." />
      </div>
      <PrimaryButton disabled={!qcStage || !result} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ qcStage, qcResult: result, qcInspector: inspector, qcNotes: notes, qcRework: rework },
          `QC ${qcStage}: ${result}`, notes);
        setSaving(false);
      }}>Record QC Result</PrimaryButton>
    </SectionCard>
  );
}

function ProcurementPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [fixedVendor, setFixedVendor] = useState<boolean>(fd.fixedVendor ?? false);
  const [vendorId, setVendorId] = useState<string>((fd as any).vendorId || '');
  const [vendorName, setVendorName] = useState(fd.vendorName || '');
  const [poNumber, setPoNumber] = useState(fd.poNumber || '');
  const [saving, setSaving] = useState(false);
  const [vendors, setVendors] = useState<Array<{ id: string; name: string; gstin?: string }>>([]);
  const [loadingVendors, setLoadingVendors] = useState(true);

  // Load vendor master on mount (Bug 9 fix — dropdown instead of free text)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/vendors?limit=500');
        const list = (res.data?.data || []).map((v: any) => ({ id: v.id, name: v.name, gstin: v.gstin }));
        setVendors(list);
      } catch {
        setVendors([]);
      } finally {
        setLoadingVendors(false);
      }
    })();
  }, []);

  const handleVendorChange = (id: string) => {
    setVendorId(id);
    const found = vendors.find((v) => v.id === id);
    setVendorName(found?.name || '');
  };

  return (
    <SectionCard icon={ShoppingCart} title="Procurement / Purchase Order" description="Select vendor and create PO for traded goods.">
      <div className="mb-4">
        <Label>Vendor Selection</Label>
        <RadioGroup value={fixedVendor ? 'fixed' : 'multiple'} onChange={v => setFixedVendor(v === 'fixed')} options={[
          { value: 'fixed', label: 'Fixed Vendor (Auto-generate PO)' },
          { value: 'multiple', label: 'Send Quotations to Multiple Vendors' },
        ]} />
      </div>
      <div className="mb-4">
        <Label>Vendor {loadingVendors ? '(loading…)' : `(${vendors.length} in master)`}</Label>
        <select
          value={vendorId}
          onChange={(e) => handleVendorChange(e.target.value)}
          disabled={loadingVendors}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100"
        >
          <option value="">— Select vendor from master —</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>{v.name}{v.gstin ? ` (${v.gstin})` : ''}</option>
          ))}
        </select>
        {!loadingVendors && vendors.length === 0 && (
          <p className="mt-1 text-xs text-amber-600">No vendors found. Add vendors in Procurement → Vendors first.</p>
        )}
      </div>
      <div className="mb-4">
        <Label>PO Number</Label>
        <Input value={poNumber} onChange={setPoNumber} placeholder="Purchase Order number" />
      </div>
      {!fixedVendor && (
        <div className="mb-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-700">
          Quotations to be sent to multiple vendors. Select the best offer and raise the PO.
        </div>
      )}
      <PrimaryButton disabled={!vendorId} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ fixedVendor, vendorName, poNumber, vendorId } as any, 'PO created — Vendor: ' + vendorName);
        setSaving(false);
      }}>Create PO &amp; Map to SO</PrimaryButton>
    </SectionCard>
  );
}

function GrnQcPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [grnNumber, setGrnNumber] = useState(fd.grnNumber || '');
  const [receivedQty, setReceivedQty] = useState(String(fd.grnReceivedQty || ''));
  const [inspectionStatus, setInspectionStatus] = useState(fd.grnInspectionStatus || '');
  const [inspector, setInspector] = useState(fd.grnInspector || '');
  const [notes, setNotes] = useState(fd.grnNotes || '');
  const [saving, setSaving] = useState(false);

  return (
    <SectionCard icon={Package} title="GRN & Quality Control" description="Record goods received note and inspection at PO receipt.">
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label>GRN Number</Label>
          <Input value={grnNumber} onChange={setGrnNumber} placeholder="GRN-001" />
        </div>
        <div>
          <Label>Received Qty</Label>
          <Input value={receivedQty} onChange={setReceivedQty} type="number" placeholder="0" />
        </div>
      </div>
      <div className="mb-4">
        <Label>Inspection Status</Label>
        <RadioGroup value={inspectionStatus} onChange={setInspectionStatus} options={[
          { value: 'pass', label: 'Pass' },
          { value: 'partial', label: 'Partial' },
          { value: 'fail', label: 'Fail' },
        ]} />
      </div>
      <div className="mb-4">
        <Label>Inspector</Label>
        <Input value={inspector} onChange={setInspector} placeholder="Inspector name" />
      </div>
      <div className="mb-4">
        <Label>Notes</Label>
        <Textarea value={notes} onChange={setNotes} placeholder="Inspection findings, damage notes..." />
      </div>
      <PrimaryButton disabled={!grnNumber || !inspectionStatus} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance(
          { grnNumber, grnReceivedQty: Number(receivedQty), grnInspectionStatus: inspectionStatus, grnInspector: inspector, grnNotes: notes },
          `GRN ${grnNumber} — Inspection: ${inspectionStatus}`,
          notes,
        );
        setSaving(false);
      }}>Confirm GRN &amp; QC</PrimaryButton>
    </SectionCard>
  );
}

function AllocatePanel({ fd, so, onAdvance }: { fd: FulfilmentData; so: SalesOrder; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [location, setLocation] = useState(fd.warehouseLocation || '');
  const [allocatedQty, setAllocatedQty] = useState(String(fd.allocatedQty || ''));
  const [saving, setSaving] = useState(false);

  const totalQty = so.items.reduce((sum, i) => sum + Number(i.quantity), 0);

  return (
    <SectionCard icon={MapPin} title="Allocate Stock to SO" description="Locate stock in the warehouse and allocate it to this sales order.">
      <div className="mb-4 rounded-lg p-4" style={{ background: 'var(--bg-muted)' }}>
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Total Order Qty</p>
        <p className="mt-1 text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{totalQty} units</p>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label>Warehouse / Rack Location</Label>
          <Input value={location} onChange={setLocation} placeholder="Warehouse A — Rack B3" />
        </div>
        <div>
          <Label>Allocated Qty</Label>
          <Input value={allocatedQty} onChange={setAllocatedQty} type="number" placeholder={String(totalQty)} />
        </div>
      </div>
      <PrimaryButton disabled={!location} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ warehouseLocation: location, allocatedQty: Number(allocatedQty) || totalQty }, 'Stock allocated — ' + location);
        setSaving(false);
      }}>Allocate to SO</PrimaryButton>
    </SectionCard>
  );
}

function DispatchPanel({ fd, so, onAdvance }: { fd: FulfilmentData; so: SalesOrder; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [photos, setPhotos] = useState<string[]>(fd.dispatchPhotos || ['']);
  const [carrier, setCarrier] = useState(fd.carrier || '');
  const [vehicleNo, setVehicleNo] = useState(fd.vehicleNo || '');
  const [driverName, setDriverName] = useState(fd.driverName || '');
  const [driverPhone, setDriverPhone] = useState(fd.driverPhone || '');
  const [estDelivery, setEstDelivery] = useState(fd.estimatedDelivery || '');
  const [lrNumber, setLrNumber] = useState(fd.lrNumber || '');
  const [saving, setSaving] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);

  const addPhoto = () => setPhotos([...photos, '']);
  const updatePhoto = (i: number, v: string) => { const p = [...photos]; p[i] = v; setPhotos(p); };
  const removePhoto = (i: number) => { const p = photos.filter((_, idx) => idx !== i); setPhotos(p); };

  const cleanPhotos = photos.filter(p => p.trim());
  const hasPhotos = cleanPhotos.length > 0;

  const generateInvoice = async () => {
    setGeneratingInvoice(true);
    try {
      const res = await api.post(`/sales-orders/${so.id}/invoice`, {
        items: so.items.map(item => ({ quantity: item.quantity })),
      });
      if (res.data.success) {
        alert(`Invoice created: ${res.data.data.number}`);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to create invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  return (
    <SectionCard icon={Truck} title="Dispatch Order" description="Upload mandatory dispatch photos, assign transport, and generate invoice.">
      <div className="mb-4 rounded-lg border p-4" style={{ background: 'var(--primary-50)', borderColor: 'var(--primary-200)' }}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-[var(--ink-0)]">Generate Invoice Before Dispatch</p>
            <p className="text-xs text-[var(--ink-0)]">Create invoice to initiate billing</p>
          </div>
          <button
            onClick={generateInvoice}
            disabled={generatingInvoice}
            className="rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
          >
            {generatingInvoice ? 'Generating...' : 'Generate Invoice'}
          </button>
        </div>
      </div>

      <div className="mb-4">
        <Label>Dispatch Photos (Mandatory - At least 1 required)</Label>
        {photos.map((p, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <Input value={p} onChange={v => updatePhoto(i, v)} placeholder="https://..." className="flex-1" />
            {photos.length > 1 && (
              <button onClick={() => removePhoto(i)} className="text-red-500 hover:text-red-700 p-1">
                <XCircle size={18} />
              </button>
            )}
          </div>
        ))}
        <button onClick={addPhoto} className="mt-1 flex items-center gap-1.5 text-sm text-[var(--ink-0)] hover:underline">
          <Camera size={14} /> Add photo URL
        </button>
        {!hasPhotos && (
          <p className="mt-2 text-xs text-red-500">At least one dispatch photo is required before marking as dispatched</p>
        )}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div><Label>Carrier / Transport Company</Label><Input value={carrier} onChange={setCarrier} placeholder="DHL, Blue Dart, Self..." /></div>
        <div><Label>Vehicle Number</Label><Input value={vehicleNo} onChange={setVehicleNo} placeholder="MH12AB1234" /></div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div><Label>Driver Name</Label><Input value={driverName} onChange={setDriverName} placeholder="Driver name" /></div>
        <div><Label>Driver Phone</Label><Input value={driverPhone} onChange={setDriverPhone} placeholder="+91 98XXX XXXXX" /></div>
      </div>
      <div className="mb-4 grid grid-cols-2 gap-4">
        <div><Label>LR Number</Label><Input value={lrNumber} onChange={setLrNumber} placeholder="LR Number" /></div>
        <div><Label>Estimated Delivery</Label><Input value={estDelivery} onChange={setEstDelivery} type="date" /></div>
      </div>
      <div className="mb-4 rounded-lg p-4" style={{ background: 'var(--bg-muted)' }}>
        <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Order Total</p>
        <p className="mt-1 text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(Number(so.total))}</p>
      </div>
      <PrimaryButton disabled={!carrier || !hasPhotos} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance(
          { dispatchPhotos: cleanPhotos, carrier, vehicleNo, driverName, driverPhone, estimatedDelivery: estDelivery, lrNumber },
          `Order dispatched — ${carrier} ${vehicleNo}, LR: ${lrNumber}`,
        );
        setSaving(false);
      }}>Mark as Dispatched</PrimaryButton>
    </SectionCard>
  );
}

function TransitPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string) => Promise<void> }) {
  const [saving, setSaving] = useState(false);

  const trackingStages = [
    { key: 'dispatched', label: 'Dispatched', time: fd.dispatchedAt },
    { key: 'in_transit', label: 'In Transit', time: null },
    { key: 'out_for_delivery', label: 'Out for Delivery', time: null },
    { key: 'delivered', label: 'Delivered', time: fd.deliveredAt },
  ];

  return (
    <SectionCard icon={Truck} title="Shipment Tracking" description="Monitor delivery progress in real-time.">
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Shipment Status</p>
          <span className="rounded-full px-2.5 py-0.5 text-xs font-medium bg-blue-50 text-blue-700">In Transit</span>
        </div>
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" />
          <div className="space-y-6">
            {trackingStages.map((stage, idx) => {
              const isCompleted = idx < 2;
              const isCurrent = idx === 2;
              return (
                <div key={stage.key} className="flex items-start gap-4 relative z-10">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${isCompleted ? 'bg-green-500 border-green-500' : isCurrent ? 'bg-blue-500 border-blue-500' : 'bg-white border-gray-300'}`}>
                    {isCompleted ? <CheckCircle2 size={14} className="text-white" /> : isCurrent ? <Truck size={14} className="text-white" /> : <Circle size={14} className="text-gray-400" />}
                  </div>
                  <div className="flex-1 pt-1">
                    <p className={`text-sm font-medium ${isCompleted || isCurrent ? '' : 'text-gray-400'}`} style={{ color: isCompleted || isCurrent ? 'var(--text-primary)' : undefined }}>{stage.label}</p>
                    {stage.time && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(stage.time).toLocaleString()}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {fd.carrier && (
        <div className="mb-4 rounded-lg border p-4 space-y-2" style={{ borderColor: 'var(--border-primary)' }}>
          <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Transport Details</p>
          {fd.carrier && <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ color: 'var(--text-muted)' }}>Carrier:</span> {fd.carrier}</p>}
          {fd.vehicleNo && <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ color: 'var(--text-muted)' }}>Vehicle:</span> {fd.vehicleNo}</p>}
          {fd.driverName && <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ color: 'var(--text-muted)' }}>Driver:</span> {fd.driverName}</p>}
          {fd.lrNumber && <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ color: 'var(--text-muted)' }}>LR No:</span> {fd.lrNumber}</p>}
          {fd.estimatedDelivery && <p className="text-sm" style={{ color: 'var(--text-primary)' }}><span style={{ color: 'var(--text-muted)' }}>ETA:</span> {formatDate(fd.estimatedDelivery)}</p>}
        </div>
      )}

      {fd.dispatchPhotos && fd.dispatchPhotos.length > 0 && (
        <div className="mb-4">
          <Label>Dispatch Photos</Label>
          <div className="flex flex-wrap gap-2">
            {fd.dispatchPhotos.map((p, i) => (
              <a key={i} href={p} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 rounded border px-2 py-1 text-xs text-[var(--ink-0)] hover:underline" style={{ borderColor: 'var(--border-primary)' }}>
                <Camera size={12} /> Photo {i + 1}
              </a>
            ))}
          </div>
        </div>
      )}
      <PrimaryButton loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ deliveredAt: new Date().toISOString() }, 'Goods delivered — marked as delivered');
        setSaving(false);
      }}>Mark as Delivered</PrimaryButton>
    </SectionCard>
  );
}

function DeliveryPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string) => Promise<void> }) {
  const [photos, setPhotos] = useState<string[]>(fd.deliveryPhotos || ['']);
  const [invoiceRef, setInvoiceRef] = useState(fd.customerInvoiceRef || '');
  const [receiverName, setReceiverName] = useState(fd.receiverName || '');
  const [receiverPhone, setReceiverPhone] = useState(fd.receiverPhone || '');
  const [saving, setSaving] = useState(false);

  const addPhoto = () => setPhotos([...photos, '']);
  const updatePhoto = (i: number, v: string) => { const p = [...photos]; p[i] = v; setPhotos(p); };
  const removePhoto = (i: number) => { const p = photos.filter((_, idx) => idx !== i); setPhotos(p); };

  const cleanPhotos = photos.filter(p => p.trim());
  const hasPhotos = cleanPhotos.length > 0;

  return (
    <SectionCard icon={CheckCircle2} title="Delivery Confirmation" description="Upload mandatory delivery photos and customer invoice reference.">
      <div className="mb-4">
        <Label>Delivery Photos (Mandatory - At least 1 required)</Label>
        {photos.map((p, i) => (
          <div key={i} className="mb-2 flex items-center gap-2">
            <Input value={p} onChange={v => updatePhoto(i, v)} placeholder="https://..." className="flex-1" />
            {photos.length > 1 && (
              <button onClick={() => removePhoto(i)} className="text-red-500 hover:text-red-700 p-1">
                <XCircle size={18} />
              </button>
            )}
          </div>
        ))}
        <button onClick={addPhoto} className="mt-1 flex items-center gap-1.5 text-sm text-[var(--ink-0)] hover:underline">
          <Camera size={14} /> Add photo URL
        </button>
        {!hasPhotos && (
          <p className="mt-2 text-xs text-red-500">At least one delivery photo is required to confirm delivery</p>
        )}
      </div>

      <div className="mb-4 grid grid-cols-2 gap-4">
        <div>
          <Label>Receiver Name</Label>
          <Input value={receiverName} onChange={setReceiverName} placeholder="Person who received goods" />
        </div>
        <div>
          <Label>Receiver Phone</Label>
          <Input value={receiverPhone} onChange={setReceiverPhone} placeholder="+91 98XXX XXXXX" />
        </div>
      </div>

      <div className="mb-4">
        <Label>Customer Invoice / Delivery Note Reference (Mandatory)</Label>
        <Input value={invoiceRef} onChange={setInvoiceRef} placeholder="Customer-side invoice or delivery note number" />
      </div>

      <div className="mb-4 rounded-lg border p-4" style={{ background: 'var(--bg-muted)', borderColor: 'var(--border-primary)' }}>
        <p className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>Delivery Validation</p>
        <p className="text-sm mt-1" style={{ color: 'var(--text-primary)' }}>
          {hasPhotos && invoiceRef ? '✓ All required documents uploaded' : '⚠ Awaiting required documents'}
        </p>
      </div>

      <PrimaryButton disabled={!hasPhotos || !invoiceRef} loading={saving} onClick={async () => {
        setSaving(true);
        await onAdvance({ 
          deliveryPhotos: cleanPhotos, 
          customerInvoiceRef: invoiceRef,
          receiverName,
          receiverPhone,
          deliveryValidatedAt: new Date().toISOString(),
        }, 'Delivery confirmed — photos and customer invoice uploaded');
        setSaving(false);
      }}>Confirm Delivery</PrimaryButton>
    </SectionCard>
  );
}

function ResolutionPanel({ fd, onAdvance }: { fd: FulfilmentData; onAdvance: (data: Partial<FulfilmentData>, action: string, notes?: string, _path?: string, nextStage?: string) => Promise<void> }) {
  const [type, setType] = useState(fd.resolutionType || '');
  const [adjNotes, setAdjNotes] = useState(fd.adjustmentNotes || '');
  const [debitRef, setDebitRef] = useState(fd.debitNoteRef || '');
  const [creditRef, setCreditRef] = useState(fd.creditNoteRef || '');
  const [saving, setSaving] = useState(false);

  return (
    <SectionCard icon={AlertTriangle} title="Resolution Check" description="Confirm delivery accuracy. Handle any quantity mismatches.">
      <div className="mb-4">
        <Label>Delivery Outcome</Label>
        <div className="space-y-2">
          {[
            { value: 'all_clear', label: 'All Clear — Delivery matches dispatch slip' },
            { value: 'qty_mismatch', label: 'Quantity Mismatch in Dispatch Slip' },
          ].map(opt => (
            <label key={opt.value} className="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-all" style={{ borderColor: type === opt.value ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)', background: type === opt.value ? 'var(--primary-50, #eff6ff)' : 'var(--bg-surface)' }}>
              <input type="radio" value={opt.value} checked={type === opt.value} onChange={() => setType(opt.value)} className="accent-[var(--ink-0)]" />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
      {type === 'qty_mismatch' && (
        <div className="mb-4 space-y-3">
          <Label>Resolution Method</Label>
          {[
            { value: 'settle_next', label: 'Settle in Next Order' },
            { value: 'debit_note', label: 'Issue Debit Note' },
            { value: 'credit_note', label: 'Issue Credit Note' },
          ].map(opt => (
            <label key={opt.value} className="flex cursor-pointer items-center gap-3 rounded-lg border px-4 py-3 transition-all" style={{ borderColor: adjNotes === opt.value ? 'var(--primary-600, #2563eb)' : 'var(--border-primary)', background: 'var(--bg-surface)' }}>
              <input type="radio" value={opt.value} checked={adjNotes === opt.value} onChange={() => setAdjNotes(opt.value)} className="accent-[var(--ink-0)]" />
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{opt.label}</span>
            </label>
          ))}
          {adjNotes === 'debit_note' && (
            <div><Label>Debit Note Reference</Label><Input value={debitRef} onChange={setDebitRef} placeholder="DN-001" /></div>
          )}
          {adjNotes === 'credit_note' && (
            <div><Label>Credit Note Reference</Label><Input value={creditRef} onChange={setCreditRef} placeholder="CN-001" /></div>
          )}
        </div>
      )}
      <div className="flex gap-3">
        {type === 'all_clear' && (
          <PrimaryButton loading={saving} onClick={async () => {
            setSaving(true);
            await onAdvance({ resolutionType: 'all_clear' }, 'All clear — order fulfilled', undefined, undefined, 'fulfilled');
            setSaving(false);
          }}>Mark as Fulfilled</PrimaryButton>
        )}
        {type === 'qty_mismatch' && adjNotes && (
          <PrimaryButton loading={saving} onClick={async () => {
            setSaving(true);
            await onAdvance({ resolutionType: type, adjustmentNotes: adjNotes, debitNoteRef: debitRef, creditNoteRef: creditRef },
              `Mismatch resolved: ${adjNotes}`, adjNotes, undefined, 'fulfilled');
            setSaving(false);
          }}>Resolve &amp; Fulfil</PrimaryButton>
        )}
      </div>
    </SectionCard>
  );
}

function FulfilledPanel({ so }: { so: SalesOrder }) {
  return (
    <SectionCard icon={ThumbsUp} title="Order Fulfilled!" description="This order has been successfully completed.">
      <div className="rounded-lg p-6 text-center" style={{ background: 'var(--bg-muted)' }}>
        <CheckCircle2 size={48} className="mx-auto mb-3 text-green-500" />
        <p className="mb-1 text-lg font-bold" style={{ color: 'var(--text-primary)' }}>{so.number} — Delivered</p>
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          {so.contact ? `${so.contact.firstName} ${so.contact.lastName}` : so.account?.name || '—'}
        </p>
        <p className="mt-3 text-2xl font-bold text-green-600">{formatINR(Number(so.total))}</p>
      </div>
    </SectionCard>
  );
}

// ==================== MAIN PAGE ====================

export function SalesOrderFulfilmentPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [so, setSo] = useState<SalesOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchSO = useCallback(async () => {
    if (!id) return;
    try {
      setError(null);
      const [statusRes, soRes] = await Promise.all([
        api.get(`/so-fulfilment/${id}/status`),
        api.get(`/sales-orders/${id}`).catch(() => null),
      ]);

      const statusData = statusRes.data?.data;
      const soData = soRes?.data?.data ?? {};

      const payment = statusData?.payment ?? {};
      const dispatch = statusData?.dispatch ?? {};
      const delivery = statusData?.delivery ?? {};
      const resolution = statusData?.resolution ?? {};
      const shipments = statusData?.shipments ?? [];
      const latestShipment = shipments[0] ?? {};
      const timeline: TimelineEntry[] = [];

      if (dispatch?.dispatchedAt) {
        timeline.push({
          stage: 'dispatch',
          action: 'Order dispatched',
          timestamp: dispatch.dispatchedAt,
          notes: dispatch.lrNumber || undefined,
        });
      }
      if (delivery?.deliveredAt) {
        timeline.push({
          stage: 'delivery',
          action: 'Delivery confirmed',
          timestamp: delivery.deliveredAt,
          notes: delivery.customerInvoiceRef || undefined,
        });
      }
      if (resolution?.resolvedAt) {
        timeline.push({
          stage: 'resolution',
          action: resolution.hasDiscrepancy ? 'Resolved with discrepancy' : 'All clear',
          timestamp: resolution.resolvedAt,
          notes: resolution.notes || undefined,
        });
      }

      const mappedFd: FulfilmentData = {
        timeline,
        advancePaymentStatus: payment.advance50Received === true ? 'received' : payment.isOnHold ? 'on_hold' : '',
        advancePaymentNotes: payment.holdReason || '',
        remainingPaymentStatus: payment.balance50Received === true ? 'received' : '',
        remainingPaymentNotes: '',
        allocatedQty: (statusData?.items ?? []).reduce((sum: number, i: any) => sum + Number(i.fulfilledQty || 0), 0),
        warehouseLocation: statusData?.allocation?.warehouseId || '',
        dispatchPhotos: [],
        shipmentId: dispatch.shipmentId || delivery.shipmentId || undefined,
        carrier: latestShipment.transportPartnerName || '',
        vehicleNo: dispatch.vehicleNumber || latestShipment.vehicleNumber || '',
        driverName: dispatch.driverName || latestShipment.driverName || '',
        lrNumber: dispatch.lrNumber || latestShipment.lrNumber || '',
        estimatedDelivery: latestShipment.estimatedDelivery || '',
        deliveryPhotos: [],
        customerInvoiceRef: delivery.customerInvoiceRef || '',
        receiverName: delivery.receiverName || '',
        receiverPhone: delivery.receiverPhone || '',
        resolutionType: resolution.hasDiscrepancy ? 'qty_mismatch' : 'all_clear',
        adjustmentNotes: resolution.resolutionMethod || '',
        debitNoteRef: resolution.resolutionMethod === 'debit_note' ? resolution.referenceNumber || '' : '',
        creditNoteRef: resolution.resolutionMethod === 'credit_note' ? resolution.referenceNumber || '' : '',
      };

      const mappedSo: SalesOrder = {
        id: statusData.salesOrderId,
        number: statusData.number,
        status: statusData.status,
        fulfilmentStage: mapBackendStageToUi(statusData.stage, statusData.fulfilledAt),
        fulfilmentPath: null,
        fulfilmentData: mappedFd,
        total: Number(statusData.total || 0),
        subtotal: Number(statusData.total || 0),
        contact: soData.contact || null,
        account: soData.account || null,
        items: (statusData.items ?? []).map((item: any) => ({
          id: item.id,
          description: item.description,
          quantity: Number(item.quantity || 0),
          rate: Number(item.rate || 0),
          amount: Number(item.amount || 0),
          fulfillmentSource: item.fulfillmentSource || 'in_stock',
          unit: item.product?.unit || null,
          discount: Number(item.discount || 0),
          productId: item.product?.id,
          fulfilledQty: Number(item.fulfilledQty || 0),
        })),
      } as SalesOrder;

      setSo(mappedSo);
    } catch {
      setError('Failed to load sales order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchSO(); }, [fetchSO]);

  const fd: FulfilmentData = (so?.fulfilmentData as FulfilmentData) || {};
  const currentStage = so?.fulfilmentStage || 'payment_check';
  const fulfilmentPath = so?.fulfilmentPath || null;
  const stageList = getFullStageList(fulfilmentPath);
  const currentIdx = stageList.indexOf(currentStage);

  const advanceStage = useCallback(async (
    data: Partial<FulfilmentData>,
    action: string,
    notes?: string,
    pathOverride?: string,
    nextStageOverride?: string,
  ) => {
    if (!id || !so) return;
    try {
      if (currentStage === 'discount') {
        await api.post(`/so-fulfilment/${id}/init`, {});
        showToast('success', 'Discount step recorded and fulfilment initialized');
      } else if (currentStage === 'advance_payment') {
        const res = await api.post(`/so-fulfilment/${id}/validate-advance`, {});
        if (res.data?.success === false) {
          await api.post(`/so-fulfilment/${id}/payment-followup`, {});
          showToast('error', res.data?.message || 'Advance payment pending. Follow-up triggered.');
        } else {
          showToast('success', res.data?.message || 'Advance payment validated');
        }
      } else if (currentStage === 'remaining_payment') {
        const res = await api.post(`/so-fulfilment/${id}/validate-balance`, {});
        if (res.data?.success === false) {
          await api.post(`/so-fulfilment/${id}/payment-followup`, {});
          showToast('error', res.data?.message || 'Balance payment pending. Follow-up triggered.');
        } else {
          showToast('success', res.data?.message || 'Balance payment validated');
        }
      } else if (currentStage === 'inventory') {
        const res = await api.post(`/so-fulfilment/${id}/determine-type`, {});
        showToast('success', res.data?.message || 'Product type determined');
      } else if (currentStage === 'procurement') {
        const res = await api.post(`/so-fulfilment/${id}/vendor-selection`, {});
        showToast('success', res.data?.message || 'Vendor selection/procurement updated');
      } else if (currentStage === 'manufacturing') {
        const res = await api.post(`/so-fulfilment/${id}/production-order`, {});
        showToast('success', res.data?.message || 'Manufacturing plan triggered');
      } else if (currentStage === 'qc' || currentStage === 'grn_qc') {
        const res = await api.post(`/so-fulfilment/${id}/allocate-inventory`, {});
        showToast('success', res.data?.message || 'QC recorded and inventory allocated');
      } else if (currentStage === 'payment_check') {
        const status = String(data.paymentStatus || data.advancePaymentStatus || data.remainingPaymentStatus || '');
        const isReceived = status === 'received';
        const paymentStage = fd.advancePaymentStatus === 'received' ? 'balance_50' : 'advance_50';
        const amountReceived = isReceived ? roundTwo(Number(so.total) * 0.5) : 0;
        const res = await api.post(`/so-fulfilment/${id}/payment-check`, {
          paymentStage,
          amountReceived,
          paymentRef: (notes || 'WEB-UI').slice(0, 100),
        });
        showToast('success', res.data?.data?.message || 'Payment status recorded');
      } else if (currentStage === 'allocate') {
        const res = await api.post(`/so-fulfilment/${id}/allocate-inventory`, {});
        showToast('success', res.data?.message || 'Stock allocated to sales order');
      } else if (currentStage === 'dispatch') {
        const photoUrls = (data.dispatchPhotos as string[] | undefined)?.filter(Boolean) || [];
        await api.post(`/so-fulfilment/${id}/dispatch`, {
          photoUrls,
          driverName: (data.driverName as string) || fd.driverName || 'Driver',
          vehicleNumber: (data.vehicleNo as string) || fd.vehicleNo || 'TEMP',
          lrNumber: (data.lrNumber as string) || fd.lrNumber || undefined,
          notes: notes || action,
        });
        showToast('success', 'Order dispatched');
      } else if (currentStage === 'transit') {
        showToast('success', 'Shipment in transit. Proceed to delivery confirmation when received.');
      } else if (currentStage === 'delivery') {
        const shipmentId = fd.shipmentId;
        const deliveryPhotoUrls = (data.deliveryPhotos as string[] | undefined)?.filter(Boolean) || [];
        const customerInvoiceRef = (data.customerInvoiceRef as string) || fd.customerInvoiceRef || '';
        if (!shipmentId) {
          showToast('error', 'Shipment not found. Please dispatch first.');
          return;
        }
        await api.post(`/so-fulfilment/${id}/delivery-confirm`, {
          shipmentId,
          deliveryPhotoUrls,
          customerInvoiceRef,
          receiverName: (data.receiverName as string) || fd.receiverName || 'Receiver',
          receiverPhone: (data.receiverPhone as string) || fd.receiverPhone || undefined,
        });
        showToast('success', 'Delivery confirmed');
      } else if (currentStage === 'resolution') {
        const isAllClear = (data.resolutionType as string) === 'all_clear';
        if (isAllClear) {
          const res = await api.post(`/so-fulfilment/${id}/validate-delivery`, { discrepancies: [] });
          showToast('success', res.data?.message || 'Order fulfilled successfully');
        } else {
          await api.post(`/so-fulfilment/${id}/validate-delivery`, {
            discrepancies: [{
              type: 'short_supply',
              description: notes || action || 'Dispatch discrepancy found',
            }],
          });
          const resolutionMap: Record<string, string> = {
            settle_next: 'adjust_next_order',
            debit_note: 'debit_note',
            credit_note: 'credit_note',
          };
          const chosen = resolutionMap[(data.adjustmentNotes as string) || 'settle_next'] || 'adjust_next_order';
          const amount = Number((data as any).adjustmentAmount || 0);
          const res = await api.post(`/so-fulfilment/${id}/resolve-discrepancy`, {
            type: chosen,
            amount: Number.isFinite(amount) ? amount : undefined,
            notes: notes || action,
          });
          showToast('success', res.data?.message || 'Discrepancy resolved and order fulfilled');
        }
      } else {
        showToast('error', 'Unsupported stage action');
        return;
      }
      await fetchSO();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to update stage');
    }
  }, [id, so, currentStage, fd.advancePaymentStatus, fd.customerInvoiceRef, fd.driverName, fd.receiverName, fd.receiverPhone, fd.shipmentId, fd.vehicleNo, fd.warehouseLocation, fetchSO]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading fulfilment data...</span>
      </div>
    );
  }

  if (error || !so) {
    return (
      <div className="flex flex-col items-center justify-center py-32" style={{ color: 'var(--text-muted)' }}>
        <AlertTriangle size={40} className="mb-2" />
        <p className="text-sm">{error || 'Sales order not found'}</p>
        <button onClick={() => navigate('/sales-orders')} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Back to list</button>
      </div>
    );
  }

  const customerName = so.contact
    ? `${so.contact.firstName} ${so.contact.lastName}`
    : so.account?.name || 'Unknown Customer';

  const STATUS_BADGE: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-600',
    confirmed: 'bg-blue-50 text-blue-700',
    processing: 'bg-yellow-50 text-yellow-700',
    shipped: 'bg-indigo-50 text-indigo-700',
    delivered: 'bg-green-50 text-green-700',
    cancelled: 'bg-red-50 text-red-700',
  };

  const timeline: TimelineEntry[] = fd.timeline || [];

  function renderStagePanel() {
    switch (currentStage) {
      case 'discount':
        return <DiscountPanel so={so!} fd={fd} onAdvance={advanceStage} />;
      case 'inventory':
        return <InventoryPanel so={so!} fd={fd} onAdvance={(data, action, notes, path, nextStage) => advanceStage(data, action, notes, path, nextStage)} />;
      case 'advance_payment':
        return <PaymentPanel title="50% Advance Payment" description="Validate 50% advance before manufacturing/procurement starts." icon={CreditCard}
          amountLabel="50% Advance Amount" amount={Number(so!.total) * 0.5}
          statusKey="advancePaymentStatus" notesKey="advancePaymentNotes" fd={fd} onAdvance={advanceStage} actionLabel="Validate Advance Payment" />;
      case 'manufacturing':
        return <ManufacturingPanel fd={fd} onAdvance={advanceStage} />;
      case 'qc':
        return <QCPanel fd={fd} onAdvance={advanceStage} />;
      case 'remaining_payment':
        return <PaymentPanel title="Remaining 50% Payment" description="Validate remaining payment after QC before dispatch." icon={CreditCard}
          amountLabel="Remaining Amount" amount={Number(so!.total) * 0.5}
          statusKey="remainingPaymentStatus" notesKey="remainingPaymentNotes" fd={fd} onAdvance={advanceStage} actionLabel="Validate Remaining Payment" />;
      case 'procurement':
        return <ProcurementPanel fd={fd} onAdvance={advanceStage} />;
      case 'grn_qc':
        return <GrnQcPanel fd={fd} onAdvance={advanceStage} />;
      case 'payment_check':
        return <PaymentPanel title="Payment Check" description="Verify customer payment before dispatch." icon={CreditCard}
          amountLabel="50% Advance Threshold" amount={Number(so!.total) * 0.5}
          statusKey="paymentStatus" notesKey="paymentNotes" fd={fd} onAdvance={advanceStage} actionLabel="Record Payment" />;
      case 'allocate':
        return <AllocatePanel fd={fd} so={so!} onAdvance={advanceStage} />;
      case 'dispatch':
        return <DispatchPanel fd={fd} so={so!} onAdvance={advanceStage} />;
      case 'transit':
        return <TransitPanel fd={fd} onAdvance={advanceStage} />;
      case 'delivery':
        return <DeliveryPanel fd={fd} onAdvance={advanceStage} />;
      case 'resolution':
        return <ResolutionPanel fd={fd} onAdvance={(data, action, notes, _path, nextStage) => advanceStage(data, action, notes, undefined, nextStage)} />;
      case 'fulfilled':
        return <FulfilledPanel so={so!} />;
      default:
        return <Alert type="info" message={`Unknown stage: ${currentStage}`} />;
    }
  }

  return (
    <div className="mx-auto max-w-4xl">
      {/* Toast */}
      {toast && (
        <div className={`fixed right-6 top-6 z-50 rounded-xl border px-5 py-3 text-sm font-medium shadow-lg ${toast.type === 'success' ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(`/sales-orders/${id}`)} className="rounded-lg p-1.5 transition-colors hover:opacity-70" style={{ background: 'var(--bg-muted)' }}>
            <ArrowLeft size={18} style={{ color: 'var(--text-secondary)' }} />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{so.number}</h1>
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STATUS_BADGE[so.status] || 'bg-gray-100 text-gray-600'}`}>
                {so.status.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
              {fulfilmentPath && (
                <span className="rounded-full px-2.5 py-0.5 text-xs font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>
                  {fulfilmentPath.charAt(0).toUpperCase() + fulfilmentPath.slice(1)}
                </span>
              )}
            </div>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{customerName} — Fulfilment Manager</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Activity size={14} className="text-[var(--saffron-600)]" />
          <span className="text-sm font-medium text-[var(--ink-0)]">Stage: {STAGE_LABELS[currentStage] || currentStage}</span>
        </div>
      </div>

      {/* Stage Pipeline */}
      <div className="mb-6 overflow-hidden rounded-xl border p-5" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
        <p className="mb-4 text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Fulfilment Pipeline</p>
        <div className="flex items-center overflow-x-auto pb-1">
          {stageList.map((stage, idx) => {
            const isCompleted = idx < currentIdx;
            const isCurrent = idx === currentIdx;
            const isFuture = idx > currentIdx;
            const Icon = STAGE_ICONS[stage] || Circle;
            return (
              <div key={stage} className="flex items-center flex-shrink-0">
                <div className="flex flex-col items-center gap-1">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full border-2 transition-all ${isCompleted ? 'border-green-500 bg-green-500' : isCurrent ? 'border-[var(--ink-0)] bg-[var(--ink-0)]' : 'bg-transparent'}`}
                    style={{ borderColor: isCompleted ? undefined : isCurrent ? undefined : 'var(--border-primary)' }}>
                    {isCompleted
                      ? <CheckCircle2 size={16} className="text-white" />
                      : <Icon size={14} className={isCurrent ? 'text-white' : undefined} style={{ color: isFuture ? 'var(--text-muted)' : undefined }} />
                    }
                  </div>
                  <span className="max-w-[72px] text-center text-[10px] font-medium leading-tight" style={{ color: isCurrent ? 'var(--text-primary)' : isCompleted ? 'var(--text-secondary)' : 'var(--text-muted)' }}>
                    {STAGE_LABELS[stage] || stage}
                  </span>
                </div>
                {idx < stageList.length - 1 && (
                  <ChevronRight size={14} className="mx-1 flex-shrink-0" style={{ color: idx < currentIdx ? 'var(--text-secondary)' : 'var(--border-primary)' }} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Current Stage Action Card */}
      <div className="mb-6">
        {renderStagePanel()}
      </div>

      {/* Timeline */}
      {timeline.length > 0 && (
        <div className="rounded-xl border" style={{ background: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="border-b px-5 py-3" style={{ borderColor: 'var(--border-primary)' }}>
            <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>Fulfilment History</h3>
          </div>
          <div className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
            {[...timeline].reverse().map((entry, i) => (
              <div key={i} className="flex items-start gap-4 px-5 py-4">
                <div className="mt-0.5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full" style={{ background: 'var(--bg-muted)' }}>
                  <Clock size={13} style={{ color: 'var(--text-muted)' }} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="rounded-full px-2 py-0.5 text-[10px] font-medium" style={{ background: 'var(--bg-muted)', color: 'var(--text-secondary)' }}>
                      {STAGE_LABELS[entry.stage] || entry.stage}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{new Date(entry.timestamp).toLocaleString()}</span>
                  </div>
                  <p className="mt-1 text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{entry.action}</p>
                  {entry.notes && <p className="mt-0.5 text-xs" style={{ color: 'var(--text-muted)' }}>{entry.notes}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
